import { createContext, useContext } from "react";

/*
 * Read-only "view as": staff holding central:impersonate see Central exactly as
 * another user does, and can change nothing while they do. The API is what
 * enforces that (red-panda-api lib/business/busi-view-as.mjs). This side
 * remembers the choice, sends it with every request (api.js), and holds the
 * write controls back so nobody is offered a button that can only fail.
 *
 * The choice lives in sessionStorage because that is per tab -- one tab can view
 * as a rep while another is the staff member themselves -- it survives both the
 * Auth0 login redirect and a reload, and it is gone when the tab closes.
 */

const STASH_KEY = "central.viewAs";

export const READ_ONLY_MESSAGE = "Disabled while viewing as another user";

/**
 * @returns {{ actorSub: string | null, target: number | "self" } | null}
 *   actorSub is the Auth0 user the choice belongs to, null until LoginGuard
 *   binds it (a /view-as link is followed before anyone has logged in).
 */
export const readViewAs = () => {
  try {
    const stash = JSON.parse(window.sessionStorage.getItem(STASH_KEY));
    const targetOk =
      stash?.target === "self" ||
      (Number.isInteger(stash?.target) && stash.target > 0);
    const actorOk =
      stash?.actorSub === null || typeof stash?.actorSub === "string";
    return targetOk && actorOk ? stash : null;
  } catch {
    return null;
  }
};

/** @param {{ actorSub: string | null, target: number | "self" }} stash */
export const writeViewAs = (stash) => {
  try {
    window.sessionStorage.setItem(STASH_KEY, JSON.stringify(stash));
  } catch {
    // Storage blocked: the choice still holds in LoginGuard's state until reload.
  }
};

export const clearViewAs = () => {
  try {
    window.sessionStorage.removeItem(STASH_KEY);
  } catch {
    // Nothing stored, then.
  }
};

/**
 * Forget the choice and start over at the chooser. A full page load rather than
 * a navigate(): SWR caches and page state carry no identity, so anything kept
 * would show one user's data under the other's name.
 */
export const resetViewAs = () => {
  clearViewAs();
  window.location.assign("/dashboard");
};

/**
 * readOnly  viewing as someone; every write control should be disabled.
 * canViewAs the signed-in staff member may view as others.
 * me        the user being viewed as (only while readOnly).
 */
export const ViewAsContext = createContext({
  readOnly: false,
  canViewAs: false,
  me: null,
});

export const useViewAs = () => useContext(ViewAsContext);
